import {
  clampOpenWidthRatio,
  clampSidePanelWidth,
  hasSidePanelStoredWidth,
  pixelWidthForOpenRatio,
  preferredPanelTrackForMode,
  readSidePanelStoredWidth,
  SIDE_PANEL_DEFAULT_WIDTH,
  writeSidePanelStoredWidth,
} from 'designSystem/components/sidePanelWidth'
import isNil from 'lodash/isNil'
import {
  type Dispatch,
  type RefObject,
  type SetStateAction,
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from 'react'

/**
 * Owns the openWidthRatio ↔ pixel handoff:
 * ratio mode sizes the grid track as a percentage until the user resizes or a
 * preferredWidth arrives; closing returns to ratio mode for the next open.
 * A panel with its own storageKey instead keeps the user's chosen width across
 * close/reopen and remounts, skipping ratio mode once a width has been chosen.
 */
export function useOpenWidthPreference({
  defaultWidth,
  maxWidth,
  minWidth,
  open,
  openWidthRatio,
  panelLayoutRef,
  persistStoredWidth,
  preferredWidth,
  storageKey,
}: {
  defaultWidth?: number
  maxWidth: number | undefined
  minWidth: number
  open: boolean
  openWidthRatio?: number
  panelLayoutRef: RefObject<HTMLDivElement | null>
  persistStoredWidth: boolean
  preferredWidth?: number
  storageKey?: string
}): {
  exitRatioMode: () => void
  preferredPanelTrack: string
  setWidth: Dispatch<SetStateAction<number>>
  width: number
} {
  const [width, setWidth] = useState(() =>
    readSidePanelStoredWidth(
      defaultWidth ?? SIDE_PANEL_DEFAULT_WIDTH,
      storageKey
    )
  )
  const [usesOpenWidthRatio, setUsesOpenWidthRatio] = useState(
    () =>
      !isNil(openWidthRatio) &&
      (isNil(storageKey) || !hasSidePanelStoredWidth(storageKey))
  )

  useLayoutEffect(() => {
    if (!open || !usesOpenWidthRatio || isNil(openWidthRatio)) return
    const panelLayout = panelLayoutRef.current
    if (!panelLayout) return

    const syncWidthFromContainer = () => {
      setWidth(
        pixelWidthForOpenRatio({
          containerWidth: panelLayout.getBoundingClientRect().width,
          maxWidth,
          minWidth,
          openWidthRatio,
        })
      )
    }

    syncWidthFromContainer()
    if (typeof ResizeObserver === 'undefined') return
    const observer = new ResizeObserver(syncWidthFromContainer)
    observer.observe(panelLayout)
    return () => observer.disconnect()
  }, [
    maxWidth,
    minWidth,
    open,
    openWidthRatio,
    panelLayoutRef,
    usesOpenWidthRatio,
  ])

  useEffect(() => {
    if (!open && !isNil(openWidthRatio) && isNil(storageKey)) {
      setUsesOpenWidthRatio(true)
    }
  }, [open, openWidthRatio, storageKey])

  const lastPreferredRef = useRef(preferredWidth)
  useEffect(() => {
    if (preferredWidth === lastPreferredRef.current) return
    lastPreferredRef.current = preferredWidth
    if (isNil(preferredWidth)) return
    const next = clampSidePanelWidth({
      maxWidth,
      minWidth,
      width: preferredWidth,
    })
    setUsesOpenWidthRatio(false)
    setWidth(next)
    if (persistStoredWidth) writeSidePanelStoredWidth(next, storageKey)
  }, [maxWidth, minWidth, persistStoredWidth, preferredWidth, storageKey])

  const exitRatioMode = useCallback(() => {
    setUsesOpenWidthRatio(false)
  }, [])

  return {
    exitRatioMode,
    preferredPanelTrack: preferredPanelTrackForMode({
      ratio: clampOpenWidthRatio(openWidthRatio),
      usesOpenWidthRatio,
    }),
    setWidth,
    width,
  }
}
